import { Button } from '@progress/kendo-react-buttons';
import { CardSubtitle } from '@progress/kendo-react-layout';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Row } from 'react-bootstrap';
import Webcam from 'react-webcam';
import { dataURItoBlob } from '../../utils/image-helper';
import { GameDocumentContext } from '../../contexts/game-document';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import { generateTitleById } from '../../utils/game-document/display-languages';
import ImageViewWithFullScreen from '../image-view-with-fullscreen';
import { GetTaskById } from '../../utils/game-document/assets';
import { PlayerContext } from '../../contexts/player';
import { uuid } from '../../types/common-helper';
import { Loader } from '@progress/kendo-react-indicators';
import { TFileUpload } from '../../hooks/use-upload-chunk';
import { getFromStore } from '../../utils/indexedDB';
import { GetTaskContentById } from '../../utils/game-document/assets/task-contents';

const generateRandomString = () => {
  return uuid().split('-').join('').substring(0, 5);
};

type Props = {
  isUploading: boolean;
  isReadonly?: boolean;
  selectedImage?: string;
  onSelectedImageCallback?: (blobUrl: string | File, size?: number) => void;
  isDisabled?: boolean;
  isFileUploaded?: boolean;
  formId: string;
  taskId: string;
  isOnline: boolean;
  uploadStatus?: 'idle' | 'uploading' | 'failed' | 'complete';
  uploadError?: string | null;
  onRetry?: () => void;
  onCancel?: () => void;
};

const AnswerFile = ({
  isUploading,
  isReadonly = false,
  selectedImage = '',
  onSelectedImageCallback = () => {},
  isDisabled = false,
  isFileUploaded = false,
  formId,
  taskId,
  isOnline,
  uploadStatus = 'idle',
  uploadError = null,
  onRetry,
  onCancel
}: Props) => {
  const [gameDocument] = useContext(GameDocumentContext);
  const [player] = useContext(PlayerContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);

  const videoRef = useRef<Webcam>(null);
  const inputFile = useRef<HTMLInputElement>(null);
  const [blobImage, setBlobImage] = useState<string>(selectedImage);
  const [isDisplayWebcam, setIsDisplayWebcam] = useState<boolean>(false);
  const [imageExtension, setImageExtension] = useState<string>('');
  const [fileUpload, setFileUpload] = useState<File>();
  const [isFileExist, setIsFileExist] = useState(false);

  const playerCode = player.playerState?.code;

  const handleSelectImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      let taskName = '';
      const task = GetTaskById(gameDocument.gameDocument, taskId);
      if (task) {
        taskName = task.name;
      } else {
        const taskContent = GetTaskContentById(
          gameDocument.gameDocument,
          taskId
        );
        taskName = taskContent?.name ?? '';
      }
      const fileName = file.name;
      const fileExtension = fileName.slice(fileName.lastIndexOf('.'));
      const newFileName = `${player!.playerState!.name}-${taskName}-${generateRandomString()}${fileExtension}`;

      const newFile = new File([file], newFileName, { type: file.type });
      setBlobImage(URL.createObjectURL(newFile));
      onSelectedImageCallback(newFile, newFile.size ?? 0);
      setFileUpload(newFile);
      setIsFileExist(true);
    }
  };
  const onAddImageClick = () => inputFile.current && inputFile.current.click();

  const onTakePictureClickHandler = () => {
    const screenShoot = videoRef.current?.getScreenshot();
    if (screenShoot && screenShoot !== '') {
      fetch(screenShoot)
        .then((result) => {
          const imageFile = dataURItoBlob(result.url);
          onSelectedImageCallback(imageFile as File, imageFile.size ?? 0);
          setBlobImage(result.url);
        })
        .catch((error) => console.error(error))
        .finally(() => setIsDisplayWebcam(false));
    }
  };

  const getFileExtension = (filename: string) => {
    // get file extension
    const extension = filename.substring(
      filename.lastIndexOf('.') + 1,
      filename.length
    );
    return extension;
  };
  const allowedImages = () => {
    return (
      getFileExtension(selectedImage) === 'png' ||
      getFileExtension(selectedImage) === 'jpg' ||
      getFileExtension(selectedImage) === 'jpeg' ||
      getFileExtension(selectedImage) === 'gif' ||
      getFileExtension(selectedImage) === ''
    );
  };

  const allowedVideo = () => {
    return (
      getFileExtension(selectedImage) === 'mp4' ||
      getFileExtension(selectedImage) === 'mov' ||
      getFileExtension(selectedImage) === 'wmv' ||
      getFileExtension(selectedImage) === 'avi'
    );
  };

  useEffect(() => {
    if (!playerCode) return;

    (async () => {
      const files = await getFromStore<TFileUpload>('upload-queue', playerCode);

      if (files) {
        const selectedFile = files.files.find(
          (f) => f.taskId === taskId && f.formId === formId
        );

        if (selectedFile) {
          setIsFileExist(true);

          if (selectedFile.status === 'complete') {
            if (selectedFile.url) onSelectedImageCallback(selectedFile.url);
          }
        }
      }
    })();
  }, [playerCode, taskId, formId, isUploading, isOnline]);

  return (
    <div id={'answer-file'} className="d-flex flex-column flex-grow-1 h-100">
      <div
        className={
          'd-flex flex-column justify-content-center border text-center position-relative'
        }
        style={{
          flex: 1,
          overflow: 'hidden'
        }}>
        {isDisplayWebcam ? (
          <Webcam
            id={'answer-webcam'}
            className={'py-2'}
            audio={false}
            videoConstraints={{
              height: 480,
              width: 480,
              facingMode: { exact: 'environment' }
            }}
            screenshotFormat={'image/jpeg'}
            autoPlay={true}
            ref={videoRef}></Webcam>
        ) : (
          <>
            <div
              className={`${isUploading ? 'd-flex' : 'd-none'} flex-column justify-content-center align-items-center h-100 p-3`}>
              <Loader type={'infinite-spinner'} size={'large'} />
              <span className="mt-2">
                {generateTitleById(
                  '71a2dffb-7ae7-422f-8423-93b0d4f15a4a',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Uploading...'}
              </span>
              {onCancel && (
                <Button
                  className="mt-3 btn-sm"
                  fillMode="outline"
                  themeColor="base"
                  onClick={onCancel}>
                  Cancel
                </Button>
              )}
            </div>

            {!isOnline && (isFileExist || uploadStatus === 'failed') ? (
              <div className="d-flex flex-column justify-content-center align-items-center h-100 p-3 text-center">
                <span className="material-symbols-outlined text-primary text-size-64 mb-2">
                  cloud_off
                </span>
                <span className="text-primary fw-bold text-size-18 mb-1">
                  Offline - File saved!
                </span>
                <span className="text-muted small">
                  Your file is safely stored on this device. Upload will automatically resume once you're back online.
                </span>
              </div>
            ) : uploadStatus === 'failed' && !isUploading ? (
              <div className="d-flex flex-column justify-content-center align-items-center h-100 p-3 text-center">
                <span className="material-symbols-outlined text-danger text-size-48 mb-2">
                  error
                </span>
                <span className="text-danger fw-bold mb-1">
                  {uploadError ||
                    'Upload failed. Please check your connection and retry.'}
                </span>
                <span className="text-muted small mb-3">
                  Your file is preserved. You can retry upload or choose a new file.
                </span>
                {onRetry && (
                  <Button
                    themeColor="primary"
                    className="px-3 py-2"
                    onClick={onRetry}>
                    <span className="material-symbols-outlined mr-2">refresh</span>
                    Retry Upload
                  </Button>
                )}
              </div>
            ) : null}

            <div
              className={`${isUploading || uploadStatus === 'failed' || (!isOnline && isFileExist) ? 'd-none' : 'd-flex'} justify-content-center align-items-center`}>
              {blobImage === '' || selectedImage === '' ? (
                <span
                  className={
                    'material-symbols-outlined text-success text-size-64'
                  }>
                  wallpaper
                </span>
              ) : allowedImages() ? (
                <ImageViewWithFullScreen
                  src={selectedImage ?? blobImage}
                  alt={'preview'}>
                  <img
                    src={selectedImage ?? blobImage}
                    alt={'preview'}
                    className={'img-fluid media-image'}
                    draggable={false}
                    style={{
                      objectFit: 'contain'
                    }}
                  />
                </ImageViewWithFullScreen>
              ) : allowedVideo() ? (
                <video
                  controls
                  preload={'metadata'}
                  crossOrigin={'anonymous'}
                  className={'img-fluid img-thumbnail '}
                  key={selectedImage}>
                  <source
                    src={selectedImage}
                    type={`video/${
                      imageExtension !== ''
                        ? imageExtension
                        : getFileExtension(selectedImage)
                    }`}
                  />
                </video>
              ) : (
                <p>
                  <span
                    className={
                      'material-symbols-outlined text-primary text-size-64'
                    }>
                    cloud_done
                  </span>
                  <br />
                  {!isReadonly ? (
                    (fileUpload?.name ??
                    decodeURI(
                      selectedImage.substring(
                        selectedImage.lastIndexOf('/') + 1
                      )
                    ))
                  ) : (
                    <>
                      <span>
                        {decodeURI(
                          selectedImage.substring(
                            selectedImage.lastIndexOf('/') + 1
                          )
                        )}
                      </span>{' '}
                      <br />
                      <a href={selectedImage}>Download</a>
                    </>
                  )}
                </p>
              )}
            </div>
            {blobImage === '' && (
              <CardSubtitle>
                {' '}
                {generateTitleById(
                  '567963b7-47c0-40e9-8659-2e43a5a5befb',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Preview'}
              </CardSubtitle>
            )}
          </>
        )}
      </div>

      {!isReadonly && (
        <Row className={'py-2 justify-content-center'}>
          <div className={'d-flex flex-column gap-2'}>
            <Button
              className={'w-100 upload-button p-2'}
              onClick={
                isDisplayWebcam ? onTakePictureClickHandler : onAddImageClick
              }
              disabled={isDisabled || isUploading}>
              <div
                className={
                  'd-flex justify-content-center align-items-center gap-3'
                }>
                <span
                  className={'material-symbols-outlined mr-2 text-success'}
                  role={'button'}>
                  add_photo_alternate
                </span>
                <span>
                  {isDisplayWebcam
                    ? generateTitleById(
                        '7b63892e-236c-4b6a-bfec-d5d3d66794af',
                        gameDocument,
                        displayLanguageContext.displayLanguageSelected
                          .resources!,
                        'game'
                      ) || 'Take picture'
                    : generateTitleById(
                        'b6d7d97b-37bd-4ef9-aa9e-0e02a404a375',
                        gameDocument,
                        displayLanguageContext.displayLanguageSelected
                          .resources!,
                        'game'
                      ) || 'Add from files'}
                </span>
              </div>
            </Button>
            <input
              type={'file'}
              id={'answer-file-upload'}
              className={'d-none'}
              onChange={handleSelectImage}
              onClick={(event) => {
                event.currentTarget.value = '';
              }}
              ref={inputFile}
              accept={'*'}
            />
          </div>
        </Row>
      )}
    </div>
  );
};

export default AnswerFile;
